import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  MessageCircle, 
  Send, 
  X, 
  Minimize2, 
  Maximize2,
  Bot,
  User,
  Loader2,
  AlertTriangle,
  Shield,
  MapPin,
  Navigation,
  Trash2,
  Sparkles,
  Radio,
  CloudLightning,
  Route,
  Volume2,
  VolumeX,
  Mic,
  Headphones,
  Lock,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import TankAvatar from './TankAvatar';
import DaisyAvatar from './DaisyAvatar';
import { VoiceSettingsButton } from './VoiceSettingsPanel';
import useVoice from '@/hooks/useVoice';
import { useSubscription } from '@/contexts/SubscriptionContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick action suggestions
const QUICK_ACTIONS = [
  { icon: Shield, label: "Is it safe near me?", prompt: "Is it safe in my current area right now?" },
  { icon: AlertTriangle, label: "Active threats", prompt: "What are the most serious active threats right now?" },
  { icon: Radio, label: "Scanner summary", prompt: "Summarize the current police scanner activity" },
  { icon: CloudLightning, label: "Weather alerts", prompt: "Are there any severe weather alerts I should know about?" },
  { icon: Route, label: "Safe route", prompt: "What areas should I avoid right now?" }
];

// Message component with Tank/Daisy support
const ChatMessage = ({ message, isUser, isLoading, character = 'tank' }) => {
  const isDaisy = character === 'daisy' || message?.character === 'daisy';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {isUser ? (
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-teal-600">
          <User className="w-4 h-4 text-white" />
        </div>
      ) : (
        <div className="flex-shrink-0">
          {isDaisy ? (
            <DaisyAvatar size="sm" showTooltip={true} />
          ) : (
            <TankAvatar size="sm" showTooltip={true} />
          )}
        </div>
      )}
      
      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className={`rounded-2xl px-4 py-2 ${
          isUser 
            ? 'bg-teal-600 text-white rounded-br-md' 
            : 'bg-slate-800 text-slate-200 rounded-bl-md'
        }`}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{isDaisy ? 'Daisy is responding...' : 'Tank is thinking...'}</span>
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-sm">{message.text}</div>
          )}
        </div>
        <div className={`text-xs text-slate-500 mt-1 ${isUser ? 'text-right' : ''}`}>
          {message.timestamp}
        </div>
      </div>
    </div>
  );
};

// Locked state component for free users
const LockedAIChat = ({ onClose }) => {
  const { canStartTrial, startTrial } = useSubscription();
  const [loading, setLoading] = React.useState(false);
  
  const handleStartTrial = async () => {
    setLoading(true);
    const result = await startTrial();
    setLoading(false);
    // Chat will automatically unlock if trial starts successfully
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 flex flex-col glass-card border border-slate-700 rounded-2xl overflow-hidden shadow-2xl" data-testid="ai-chat-locked">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TankAvatar size="md" showTooltip={true} />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-100">Tank</span>
                <Lock className="w-3 h-3 text-amber-400" />
              </div>
              <p className="text-xs text-slate-400">AI Intel Specialist</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-slate-400 hover:text-slate-200"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Locked Content */}
      <div className="p-6 text-center">
        <div className="inline-flex p-4 bg-teal-500/20 rounded-full mb-4">
          <Bot className="w-10 h-10 text-teal-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Meet Tank & Daisy</h3>
        <p className="text-slate-400 text-sm mb-4">
          Your 24/7 AI intelligence team. Tank's a former trucker who's seen it all. 
          Daisy's a former 911 dispatcher with nerves of steel.
        </p>
        
        <div className="bg-slate-800/50 rounded-lg p-4 mb-4 text-left">
          <p className="text-xs text-slate-500 mb-2">With AI Chat you can:</p>
          <ul className="space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-teal-400" />
              Get real-time safety assessments
            </li>
            <li className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-teal-400" />
              Summarize scanner activity
            </li>
            <li className="flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-teal-400" />
              Voice alerts read by Tank or Daisy
            </li>
            <li className="flex items-center gap-2">
              <Route className="w-4 h-4 text-teal-400" />
              Plan safe routes around incidents
            </li>
          </ul>
        </div>

        {/* Trial Button - Primary CTA */}
        {canStartTrial() && (
          <Button 
            onClick={handleStartTrial}
            disabled={loading}
            className="w-full bg-orange-600 hover:bg-orange-700 mb-3 font-bold"
            data-testid="ai-chat-start-trial"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Activating...
              </span>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                24-Hour Tactical Preview - GO!
              </>
            )}
          </Button>
        )}
        
        {/* Upgrade Button */}
        <Link to="/pricing">
          <Button className={`w-full ${canStartTrial() ? 'bg-slate-700 hover:bg-slate-600' : 'bg-teal-600 hover:bg-teal-700'}`}>
            <Zap className="w-4 h-4 mr-2" />
            {canStartTrial() ? 'Or Upgrade to Pro - $10/month' : 'Upgrade to Pro - $10/month'}
          </Button>
        </Link>
        <p className="text-xs text-slate-500 mt-2">
          {canStartTrial() ? 'No credit card required for preview' : '$80/year (save $40) • $199 lifetime'}
        </p>
      </div>
    </div>
  );
};

// Main AI Chat component
const AIChat = ({ isOpen, onClose, token, userLocation }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Subscription check
  const { hasFeature, loading: subLoading } = useSubscription();
  const canAccessAI = hasFeature('ai_chat');
  
  // Voice hook
  const { speak, isPlaying, isLoading: voiceLoading, playTankIntro, playDaisyIntro, stop } = useVoice();

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'welcome',
        text: `Hey there! I'm Tank, your AI intel specialist. I've logged 2 million miles on the road, and I've seen just about everything that can go wrong out there.

I can help you with:
• Real-time safety assessments for your area
• Understanding current threats and incidents
• Summarizing police scanner activity
• Planning safe routes around incidents
• Prepper tips and readiness advice

For critical alerts and detailed briefings, I'll hand you off to my better half, Daisy. She's a former 911 dispatcher - sharpest mind I know. When she talks, you listen.

What can I help you stay prepared for today?`,
        isUser: false,
        character: 'tank',
        timestamp: new Date().toLocaleTimeString()
      }]);
    }
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      text: text.trim(),
      isUser: true,
      timestamp: new Date().toLocaleTimeString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setShowQuickActions(false);

    try {
      // First, check for instant cached response
      const instantCheck = await axios.post(
        `${API_URL}/api/ai/instant-response`,
        {
          message: text.trim(),
          character: 'tank',
          location: userLocation || { city: 'Los Angeles', state: 'CA' }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // If we got an instant response, use it immediately
      if (instantCheck.data.has_instant) {
        const aiMessage = {
          id: (Date.now() + 1).toString(),
          text: instantCheck.data.response,
          isUser: false,
          character: instantCheck.data.character || 'tank',
          timestamp: new Date().toLocaleTimeString(),
          isInstant: true
        };

        setMessages(prev => [...prev, aiMessage]);
        setIsLoading(false);
        
        // Auto-speak the instant response if voice is enabled
        if (voiceEnabled && instantCheck.data.response) {
          const textToSpeak = instantCheck.data.response.slice(0, 500);
          speak(textToSpeak, instantCheck.data.character || 'tank');
        }
        return;
      }
      
      // No instant response - call full AI endpoint
      const response = await axios.post(
        `${API_URL}/api/ai/chat`,
        {
          messages: [{ role: 'user', content: text.trim() }],
          user_location: userLocation || { city: 'Los Angeles', state: 'CA' },
          include_context: true
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const aiMessage = {
        id: (Date.now() + 1).toString(),
        text: response.data.response || "I apologize, I couldn't process that request. Please try again.",
        isUser: false,
        character: response.data.character || 'tank',
        timestamp: new Date().toLocaleTimeString()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Auto-speak the response if voice is enabled
      if (voiceEnabled && response.data.response) {
        // Limit text length for voice (to avoid very long TTS)
        const textToSpeak = response.data.response.slice(0, 500);
        speak(textToSpeak, response.data.character || 'tank');
      }
      
      // If there's a handoff note, add it as a separate message
      if (response.data.handoff_note) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            id: (Date.now() + 2).toString(),
            text: response.data.handoff_note,
            isUser: false,
            character: 'tank',
            timestamp: new Date().toLocaleTimeString()
          }]);
        }, 1000);
      }
    } catch (error) {
      console.error('AI chat error:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        text: "Having some radio interference here. Give me a sec and try that again, would ya?",
        isUser: false,
        character: 'tank',
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, token, userLocation, voiceEnabled, speak]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuickAction = (prompt) => {
    sendMessage(prompt);
  };

  const clearChat = async () => {
    try {
      await axios.post(
        `${API_URL}/api/ai/chat/clear`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([]);
      setShowQuickActions(true);
    } catch (error) {
      console.error('Failed to clear chat:', error);
    }
  };

  if (!isOpen) return null;
  
  // Show locked state for free users
  if (!canAccessAI && !subLoading) {
    return <LockedAIChat onClose={onClose} />;
  }

  // Minimized view
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-4 right-4 z-50 glass-card border border-slate-700 rounded-2xl p-3 w-72 cursor-pointer hover:border-teal-500/50 transition-all"
        onClick={() => setIsMinimized(false)}
      >
        <div className="flex items-center gap-3">
          <TankAvatar size="md" showTooltip={false} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-200">Tank</span>
              <Badge className="bg-green-500/20 text-green-400 text-[10px]">Online</Badge>
            </div>
            <p className="text-xs text-slate-400">AI Intel Specialist</p>
          </div>
          <Maximize2 className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    );
  }

  // Full chat view
  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 h-[600px] flex flex-col glass-card border border-slate-700 rounded-2xl overflow-hidden shadow-2xl" data-testid="ai-chat-panel">
      {/* Header */}
      <div className="p-4 border-b border-slate-800 bg-gradient-to-r from-slate-900 to-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TankAvatar size="md" showTooltip={true} />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-100">Tank</span>
                <Sparkles className="w-3 h-3 text-amber-400" />
              </div>
              <p className="text-xs text-slate-400">AI Intel Specialist</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Voice toggle */}
            <Button 
              variant="ghost" 
              size="icon" 
              className={`h-8 w-8 ${voiceEnabled ? 'text-teal-400 hover:text-teal-300' : 'text-slate-400 hover:text-slate-200'}`}
              onClick={() => {
                if (isPlaying) {
                  stop();
                } else {
                  setVoiceEnabled(!voiceEnabled);
                }
              }}
              title={isPlaying ? "Stop audio" : voiceEnabled ? "Voice enabled" : "Voice disabled"}
            >
              {isPlaying ? (
                <div className="relative">
                  <Volume2 className="w-4 h-4 animate-pulse" />
                </div>
              ) : voiceEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </Button>
            {/* Hear Tank */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-teal-400"
              onClick={playTankIntro}
              disabled={voiceLoading || isPlaying}
              title="Hear Tank"
            >
              <Mic className={`w-4 h-4 ${voiceLoading ? 'animate-pulse' : ''}`} />
            </Button>
            {/* Hear Daisy */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-amber-400"
              onClick={playDaisyIntro}
              disabled={voiceLoading || isPlaying}
              title="Hear Daisy"
            >
              <Headphones className={`w-4 h-4 ${voiceLoading ? 'animate-pulse' : ''}`} />
            </Button>
            {/* Voice Settings */}
            <VoiceSettingsButton />
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-slate-200"
              onClick={clearChat}
              title="Clear chat"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-slate-200"
              onClick={() => setIsMinimized(true)}
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-slate-400 hover:text-slate-200"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isUser={message.isUser}
          />
        ))}
        
        {isLoading && (
          <ChatMessage
            message={{ text: '', timestamp: '' }}
            isUser={false}
            isLoading={true}
          />
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Actions */}
      {showQuickActions && messages.length <= 1 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-slate-500 mb-2">Quick actions:</p>
          <div className="flex flex-wrap gap-2">
            {QUICK_ACTIONS.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action.prompt)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-xs text-slate-300 transition-all"
                >
                  <Icon className="w-3 h-3 text-teal-400" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-slate-800 bg-slate-900/50">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Tank about safety..."
            className="flex-1 bg-slate-800 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-teal-500"
            disabled={isLoading}
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={isLoading || !input.trim()}
            className="bg-teal-600 hover:bg-teal-500 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 mt-2 text-center">
          Tank uses AI to provide safety insights. Always verify critical information.
        </p>
      </form>
    </div>
  );
};

// Floating AI button to open chat
export const AIFloatingButton = ({ onClick, hasUnread }) => {
  const { hasFeature } = useSubscription();
  const canAccessAI = hasFeature('ai_chat');
  
  return (
    <button
      onClick={onClick}
      className="fixed bottom-4 right-4 z-40 w-16 h-16 rounded-full shadow-lg shadow-teal-500/30 flex items-center justify-center hover:scale-110 transition-transform overflow-hidden border-3 border-teal-500"
      data-testid="ai-chat-button"
      title={canAccessAI ? "Chat with Tank - AI Intel Specialist" : "Upgrade to unlock AI Chat"}
    >
      <img 
        src="/images/tank.png" 
        alt="Tank - AI Intel Specialist" 
        className="w-full h-full object-cover"
      />
      {!canAccessAI && (
        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center">
          <Lock className="w-6 h-6 text-amber-400" />
        </div>
      )}
      {hasUnread && canAccessAI && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-[10px] text-white font-bold">!</span>
        </div>
      )}
      <div className="absolute inset-0 rounded-full border-2 border-teal-400 animate-pulse" />
    </button>
  );
};

export default AIChat;
